import { defineStore } from "pinia";
import { DocumentsApi, DocumentTypes, TemplatesDocumentsApi, TypeUsersEnum } from "@simbelapp/order-sdk";
import type { BatchDocumentLinkResponse, DocumentDownloadLinkResponse } from "@simbelapp/order-sdk";
import { useAuthStore } from "../auth/auth.store";
import { useSessionDetailsStore } from "../sessions/session-details-store";
import { useFeedback } from "~/composables/feedback/useFeedback";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { MicroserviceEnum } from "~/utils/enums/common.enums";
import type { IDocuments } from "~/utils/interfaces/documents-interfaces";
import { UserLanguageEnum } from "~/utils/enums/users.enums";

export const useDocumentsStore = defineStore("documentsStore", {
  state: (): IDocuments => ({
    listTemplates: [],
    templateById: null,
    createTemplateForm: {
      name: "[Nouveau modèle]",
      subject: null,
      itemType: null,
      content: "",
      isDefault: false,
    },
    generateDocuments: [
      {
        documentType: null,
        users: [],
        typeUsers: null,
        errors: {
          documentType: false,
          users: false,
          typeUsers: false,
        },
        isOpen: true,
      },
    ],
    generateDocumentsModal: false,
    hasGenerateErrors: {
      error: false,
      data: null,
    },
    showModalEndGenerate: false,
    showDeleteModal: false,
    multiSelect: {
      selectedItems: [],
      excludedItems: [],
      selectedAll: false,
    },
  }),
  getters: {},
  actions: {
    /** Signed link for one document. Returns null when the call was rejected. */
    async downloadDocument(documentId: string): Promise<DocumentDownloadLinkResponse | null> {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, DocumentsApi);
        return await apiInstance.downloadDocument({ documentId });
      } catch {
        return null;
      }
    },

    /** Signed links for a batch, used by the notification flow to attach them to its events. */
    async downloadBatchDocuments(
      documentIds: string[],
      expiresIn: number,
    ): Promise<BatchDocumentLinkResponse[] | null> {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, DocumentsApi);
        return await apiInstance.downloadBatchDocuments({
          downloadBatchDocumentsDto: { documentIds, expiresIn },
        });
      } catch {
        return null;
      }
    },

    /**
     * @do show modal to delete document
     * @set showDeleteModal to true
     */
    setShowDeleteDocumentModal(show: boolean) {
      this.showDeleteModal = show;
    },
    /**
     * @do delete group
     * @param id as string
     * @case multi delete -> use multiSelect from the store
     * @callback delete group by id
     * @return void
     */
    async deleteDocument(id?: string) {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, DocumentsApi);
      const sessionDetailsStore = useSessionDetailsStore();

      const feedback = useFeedback();
      if (id) {
        try {
          await apiInstance.removeSessionBatchDocuments({
            sessionId: sessionDetailsStore.sessionDetails?.sessionId as string,
            removeBatchDocumentsDto: {
              documentsIds: [id],
              selectedAll: false,
            },
          });

          feedback.success("Document(s) supprimé avec succès", "small");
        } catch {
          feedback.error("Une erreur est survenue", "small");
        }
      } else if (this.multiSelect.selectedItems.length && !this.multiSelect.selectedAll) {
        try {
          await apiInstance.removeSessionBatchDocuments({
            sessionId: sessionDetailsStore.sessionDetails?.id as string,
            removeBatchDocumentsDto: {
              documentsIds: this.multiSelect.selectedItems.map((document) => document.documentId) as string[],
              selectedAll: false,
            },
          });

          feedback.success("Document(s) supprimé(s) avec succès", "small");
        } catch {
          feedback.error("Une erreur est survenue", "small");
        }
      } else if (this.multiSelect.selectedAll) {
        try {
          await apiInstance.removeSessionBatchDocuments({
            sessionId: sessionDetailsStore.sessionDetails?.sessionId as string,
            removeBatchDocumentsDto: {
              documentsIds: this.multiSelect.excludedItems?.map((document) => document.documentId) as string[],
              selectedAll: true,
            },
          });

          feedback.success("Document(s) supprimé(s) avec succès", "small");
        } catch {
          feedback.error("Une erreur est survenue", "small");
        }
      }
    },
    /**
     * @do add document to generateDocuments
     */
    addDocument() {
      this.generateDocuments.push({
        documentType: null,
        typeUsers: null,
        users: [],
        errors: {
          documentType: false,
          typeUsers: false,
          users: false,
        },
        isOpen: true,
      });
    },
    /**
     * @do reset the store to its initial state without reloading listTemplates
     */
    softReset() {
      this.templateById = null;
      this.createTemplateForm = {
        name: "[Nouveau modèle]",
        subject: null,
        documentType: null,
        content: "",
        isDefault: false,
        itemType: "document",
      };
      this.generateDocumentsModal = false;
    },
    /**
     * @do get all templates
     * @param scope
     * @call getTemplates
     * @set listTemplates
     * @returns {Promise<void>}
     */
    async getTemplates(scope?: string): Promise<void> {
      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TemplatesDocumentsApi);
      try {
        const list = await apiInstance.getListTemplatesDocuments();

        if (list.length) {
          this.listTemplates = list;
        } else this.listTemplates = [];
      } catch {
        feedback.error("Une erreur est survenue", "small");
      }
    },
    /**
     * @do get one template
     * @param id
     * @call getOneTemplate
     * @set templateById
     * @returns {Promise<void>}
     */
    async getOneTemplate(id: string): Promise<void> {
      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TemplatesDocumentsApi);
      try {
        const template = await apiInstance.getOneTemplateDocuments({ templateId: id });
        this.templateById = template;
      } catch {
        feedback.error("Une erreur est survenue", "small");
      }
    },
    /**
     * @do delete one template
     * @param id
     * @call deleteTemplate
     * @returns {Promise<void>}
     */
    async deleteTemplate(id: string): Promise<void> {
      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TemplatesDocumentsApi);
      try {
        await apiInstance.deleteTemplateDocuments({ templateId: id });
        feedback.success("Vos modifications ont bien été sauvegardées", "small");
      } catch {
        feedback.error("Une erreur est survenue", "small");
      }
    },
    /**
     * @do update one template
     * @param templateId
     * @call updateTemplate
     * @returns {Promise<boolean>} whether the template was saved
     */
    async updateTemplate(templateId: string): Promise<boolean> {
      if (!this.createTemplateForm.subject || !this.createTemplateForm.documentType) return false;

      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TemplatesDocumentsApi);
      try {
        await apiInstance.updateTemplateDocuments({
          templateId,
          updateTemplateDocumentsDto: {
            name: this.createTemplateForm.name,
            subject: this.createTemplateForm.subject,
            content: this.createTemplateForm.content,
            documentType: this.createTemplateForm.documentType as DocumentTypes,
          },
        });
        feedback.success("Vos modifications ont bien été sauvegardées", "small");
        return true;
      } catch {
        feedback.error("Une erreur est survenue", "small");
        return false;
      }
    },
    /**
     * @do update one template
     * @param id
     * @param isDefault
     * @call updateTemplateIsDefault
     * @returns {Promise<void>}
     */
    async updateTemplateIsDefault(id: string, isDefault: boolean): Promise<void> {
      //   const feedback = useFeedback();
      //   const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TemplatesDocumentsApi);
      //   try {
      //     await apiInstance.updateTemplate({
      //       templateId: id,
      //       updateTemplateDto: {
      //         isDefault: isDefault || false,
      //       },
      //     });
      //   } catch (error) {
      //     feedback.error("Une erreur est survenue", "small");
      //   }
    },
    /**
     * @do create one template
     * @param createTemplateForm
     * @call createTemplate
     * @returns {Promise<void>}
     */
    async createTemplate() {
      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TemplatesDocumentsApi);
      try {
        if (this.createTemplateForm.subject) {
          const data = await apiInstance.createTemplateDocuments({
            createTemplateDocumentsDto: {
              name: this.createTemplateForm.name,
              subject: this.createTemplateForm.subject,
              content: this.createTemplateForm.content,
              documentType: this.createTemplateForm.documentType as DocumentTypes,
            },
          });
          feedback.success("Vos modifications ont bien été sauvegardées", "small");
          return data.templateId;
        }
      } catch {
        feedback.error("Une erreur est survenue", "small");
        return undefined;
      }
    },
    /**
     * @do set the default template
     * @param language
     * @return the default template
     */
    templateContentDefault(): string {
      const authStore = useAuthStore();

      const fr = `<h1 style="text-align: center">Attestation individuelle de formation</h1><br>
    <p>Je soussigné, représentant de l’organisme de formation {{ provider_name }}, atteste que :</p>
    <p><br>{{ attendee_name }}, salarié de l’entreprise {{ attendee_legalEntity }} a suivi la formation suivante :</p>
    <ul>
        <li><p>Nom de la formation : {{ training_name }}</p></li>
        <li><p>Dates de la formation : du {{ session_startDate }} au {{ session_endDate }}</p></li>
        <li><p>Durée de la formation : {{ attendee_attendedHours }} heures réalisées sur {{ session_durationHours }} heures prévues, réparties sur {{ session_durationDays }} jour(s)</p></li>
        <li><p>Format : {{ session_format }}</p></li>
        <li><p>Lieu de la formation {{ session_location }}</p></li>
    </ul>
    <p>Cette formation a été délivrée par {{ trainer_name }}.</p>
    <p><br>Les objectifs de compétence suivant ont été validés au terme de la formation : <br>{{ training_goals }}
        <br><br><br>Fait à Paris, le {{ generation_date }}<br><br></p>`;

      const en = `<h1 style="text-align: center"><span>Individual Training Certificate</span></h1>
          <br>
          <p><span>I, the undersigned, representative of the training organization {{provider_name}}, certify that: </span></p>
          <p><span><br>{{attendee_name}}, employee of the company {{attendee_legalEntity}}, has completed the following training:</span></p>
          <ul>
            <li><p><span>Training name: {{training_name}}</span></p></li>
            <li><p><span>Training dates: from {{session_startDate}} to {{session.endDate}}</span></p></li>
            <li><p><span>Training duration: {{attendee_attendedHours}} hours completed out of {{session_durationHours}} planned hours, spread over {{session_durationDays}} day(s)</span></p></li>
            <li><p><span>Format: {{session_format}}</span></p></li>
            <li><p><span>Training location: {{session_location}}</span></p></li>
          </ul>
          <p></p>
          <p><span>This training was delivered by {{trainer_name}}.</span></p>
          <p><span><br>The following skill objectives were validated at the end of the training: <br>{{training_goals}} <br><br><br>Issued in Paris, on {{generation_date}}<br></span></p>
        `;

      return authStore.user?.language === UserLanguageEnum.FR ? fr : en;
    },
    /**
     * @do set the default template name
     * @param language
     * @return the default template name
     */
    templateSubjectDefault(): string {
      const authStore = useAuthStore();

      const fr = `Attestation {{training_name}}`;
      const en = `Attestation for {{training_name}}`;

      return authStore.user?.language === UserLanguageEnum.FR ? fr : en;
    },
    /**
     * @do duplicate one template
     * @param id
     * @call duplicateTemplate
     * @returns {Promise<void>}
     */
    async duplicateTemplate(id: string): Promise<void> {
      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TemplatesDocumentsApi);
      try {
        await apiInstance.duplicateTemplateDocuments({ templateId: id });

        feedback.success("Vos modifications ont bien été sauvegardées", "small");
      } catch {
        feedback.error("Une erreur est survenue", "small");
      }
    },
    /**
     * @do reset errors from generateDocuments
     * @param index
     */
    resetErrors() {
      this.hasGenerateErrors.error = false;
      this.generateDocuments.forEach((document) => {
        document.errors.documentType = false;
        document.errors.typeUsers = false;
        document.errors.users = false;
      });
    },
    /**
     * @do validate the documents
     * @check if itemType, typeUsers and users are not empty
     */
    validateDocumentGenerate() {
      this.generateDocuments.forEach((document) => {
        if (!document.documentType?.templateId) {
          document.errors.documentType = true;
        }

        if (!document.typeUsers) {
          document.errors.typeUsers = true;
        }

        if (!document.users.length && document.typeUsers?.itemType === TypeUsersEnum.Selected) {
          document.errors.users = true;
        }
      });
    },
    /**
     * @do save the documents
     * @reset errors
     * @validate the documents
     * @check if there is an error
     * @call saveGenerateDocuments
     * @returns {Promise<void>}
     */
    async saveGenerateDocuments() {
      this.resetErrors();
      this.validateDocumentGenerate();

      const hasError = this.generateDocuments.some((document) => {
        return document.errors.documentType || document.errors.typeUsers || document.errors.users;
      });

      if (!hasError) {
        const feedback = useFeedback();
        const sessionDetails = useSessionDetailsStore();
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TemplatesDocumentsApi);
        try {
          const documents = this.generateDocuments.map((document) => ({
            sessionId: sessionDetails.sessionDetails?.sessionId as string,
            templateId: document.documentType?.templateId as string,
            typeUsers: document.typeUsers?.itemType as TypeUsersEnum,
            userIds: document.users.map((user) => user.userId) || [],
          }));

          const result = await apiInstance.generateBatchDocuments({
            generateBacthDocumentsDto: documents,
          });

          if (result.length) {
            this.hasGenerateErrors.error = true;

            this.hasGenerateErrors.data = result;

            return false;
          } else {
            return true;
          }
        } catch {
          feedback.error("Une erreur est survenue", "small");
        }
      } else {
        this.generateDocuments.forEach((document) => {
          document.isOpen = false;
          if (document.errors.documentType || document.errors.typeUsers || document.errors.users) {
            document.isOpen = true;
          }
        });

        return false;
      }
    },
    /**
     * @do reset the errors
     * @set hasGenerateErrors to null
     */
    resetGenerateErrors() {
      this.hasGenerateErrors.error = false;
      this.hasGenerateErrors.data = null;
    },
    /**
     * @do save the documents with error
     **/
    async saveWithError() {
      const feedback = useFeedback();
      const sessionDetails = useSessionDetailsStore();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TemplatesDocumentsApi);

      try {
        const documents = this.generateDocuments.map((document) => ({
          sessionId: sessionDetails.sessionDetails?.sessionId as string,
          templateId: document.documentType?.templateId as string,
          typeUsers: document.typeUsers?.itemType as TypeUsersEnum,
          userIds: document.users.map((user) => user.userId) || [],
        }));

        await apiInstance.generateBatchDocuments({
          generateBacthDocumentsDto: documents,
          forceGenerate: true,
        });

        feedback.waiting("Nous sommes en train de générer les documents, cela peut prendre quelques minutes.", "small");

        return true;
      } catch {
        feedback.error("Une erreur est survenue", "small");
        return false;
      }
    },

    async generateSignInSheet(sessionId: string): Promise<boolean> {
      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TemplatesDocumentsApi);

      try {
        await apiInstance.generateSignInSheet({
          sessionId,
        });
        feedback.success(`Vos feuilles d'émargements ont bien été créées`, "small");
        return true;
      } catch (error) {
        if ((error as Error | null)?.message === "Timeslots with more than 5 days are not supported") {
          feedback.error(
            "Il n'est pas possible de générer une feuille d'émargement pour une étape qui dure plus de 5 jours",
            "small",
          );
        } else {
          feedback.error("Une erreur est survenue", "small");
        }
        return false;
      }
    },
  },
});
